import {
  Button,
  Controller,
  danger,
  datesToISO,
  ErrorAlert,
  ErrorMessage,
  Header,
  Input,
  LinkBtn,
  Select,
  SmsBalanceAlert,
  success,
  timeToDate,
  trimInputValue,
  useCustomForm,
  useEffect,
  useParams,
  useState,
} from "@";

import { enumsOptions } from "./utils/helpers";
import { defaultValues, schema } from "./utils/validation";
import { getAllEnums, getMessage, getProjects, saveMessage } from "./utils/api";

const Form = () => {
  const { edit, show } = useParams();

  const links = [
    { name: "الرسائل المؤتمتة", link: "/messages/automated" },
    { name: edit ? "تعديل" : show ? "عرض" : "اضافة", link: "" },
  ];

  const [enums, setEnums] = useState([]);
  const [error, setError] = useState(false);
  const [projects, setProjects] = useState([]);

  // custom hooks
  const {
    reset,
    watch,
    errors,
    control,
    setValue,
    register,
    handleSubmit,
    isSubmitting,
  } = useCustomForm(defaultValues, schema);

  // Handle form submission
  const submit = async (data) => {
    try {
      await saveMessage(data);
      if (!edit) reset();
      success("تم الحفظ بنجاح");
    } catch (error) {
      danger("حدث خطأ أثناء الحفظ");
    }
  };

  // Function to fetch Enums
  const fetchData = async () => {
    try {
      const [enumsResponse, projectsResponse, messageResponse] =
        await Promise.all([
          getAllEnums(),
          getProjects(),
          getMessage(show || edit),
        ]);

      const enumsData = enumsResponse.data;
      setEnums(enumsData);
      const projectsData = projectsResponse.data;
      setProjects(projectsData);

      if (show || edit) {
        const messageData = messageResponse.data;
        messageData.messageDate = datesToISO(messageData.messageDate);
        messageData.messageTime = timeToDate(messageData.messageTime);
        reset(messageData);
      }

      setError(false);
    } catch (error) {
      setError(true);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Show error alert if there are validation errors
  useEffect(() => {
    if (Object.keys(errors)?.length > 0)
      danger("يوجد خطأ في البيانات المدخلة");
  }, [errors]);

  return (
    <>
      <SmsBalanceAlert />

      {/* error alert */}
      {(edit || show) && <ErrorAlert reload={fetchData} show={error} />}

      {/* header */}
      <Header
        title={`${
          edit ? "تعديل الرسالة" : show ? "عرض الرسالة" : "اضافة رسالة"
        }`}
        links={links}
      >
        {show && <LinkBtn to={`/automated/${show}/edit`} edit />}
      </Header>

      <form onSubmit={handleSubmit(submit)} className="relative">
        <div className="card grid gap-5 p-4 sm:grid-cols-2 xl:grid-cols-3">
          <div className="grid gap-5 sm:col-span-2 sm:grid-cols-2 xl:col-span-3 xl:grid-cols-3">
            <Input
              disabled={show}
              error={errors.name}
              input={register("name")}
              label="اسم مجموعة الرسائل المؤتمتة"
            />
          </div>

          <Input
            type="select"
            value={watch("messageType")}
            disabled={show}
            label="نوع الرسالة"
            error={errors.messageType}
            input={register("messageType")}
            list={enumsOptions(enums?.MessageType)}
          />

          <div>
            <label className="input-label">المشروع/ اهداء</label>
            <Controller
              name="project"
              control={control}
              render={({ field }) => (
                <Select
                  isRtl
                  {...field}
                  isClearable
                  isSearchable
                  className="mb-2"
                  placeholder=""
                  isDisabled={show}
                  options={projects}
                  value={projects?.find(
                    (option) => option.value == field?.value?.value,
                  )}
                  onChange={(option) => field.onChange(option || "")}
                />
              )}
            />
            <ErrorMessage value={errors.project?.message} />
          </div>

          <Input
            star
            type="select"
            value={watch("customerType")}
            disabled={show}
            label="نوع العميل"
            error={errors.customerType}
            input={register("customerType")}
            list={enumsOptions(enums?.CustomerType)}
          />

          <Input
            star
            min={0}
            disabled={show}
            labelA="المبلغ من"
            labelB="المبلغ الي"
            type="number-range"
            errorB={errors.amountTo}
            errorA={errors.amountFrom}
            valueB={watch("amountTo")}
            valueA={watch("amountFrom")}
            inputB={register("amountTo")}
            inputA={register("amountFrom")}
            changeB={(e) => setValue("amountTo", e.target.value)}
            changeA={(e) => setValue("amountFrom", e.target.value)}
          />

          <Input
            star
            type="select"
            value={watch("frequency")}
            label="التكرار"
            disabled={show}
            error={errors.frequency}
            input={register("frequency")}
            list={enumsOptions(enums?.Frequency)}
          />

          <Input
            star
            type="select"
            value={watch("joiningPeriod")}
            disabled={show}
            label="فترة إنضمام العميل"
            error={errors.joiningPeriod}
            input={register("joiningPeriod")}
            list={enumsOptions(enums?.JoiningPeriod)}
          />

          <Input
            star
            disabled={show}
            type="number-range"
            labelA="عدد العمليات من"
            labelB="عدد العمليات الي"
            errorB={errors.orderCountTo}
            errorA={errors.orderCountFrom}
            valueB={watch("orderCountTo")}
            valueA={watch("orderCountFrom")}
            inputB={register("orderCountTo")}
            inputA={register("orderCountFrom")}
            changeB={(e) => setValue("orderCountTo", e.target.value)}
            changeA={(e) => setValue("orderCountFrom", e.target.value)}
          />

          <Input
            star
            disabled={show}
            type="datepicker"
            dateFormat="HH:mm"
            showTimeOnly={true}
            showTimeSelect={true}
            label="زمن ارسال الرسالة"
            value={watch("messageTime")}
            change={(e) => setValue("messageTime", e)}
          />

          <Input
            star
            disabled={show}
            type="datepicker"
            label="تاريخ ارسال الرسالة"
            value={watch("messageDate")}
            change={(e) => setValue("messageDate", e)}
          />

          <div className="">
            <Input
              star
              disabled={show}
              label="prefix للرابط المختصر"
              error={errors.affiliateUrlPrefix}
              value={watch("affiliateUrlPrefix")}
              change={(e) =>
                setValue(
                  "affiliateUrlPrefix",
                  trimInputValue(e.target.value, 5),
                )
              }
            />

            {watch("affiliateUrlPrefix")?.length > 0 && (
              <div className="mt-3 flex flex-wrap justify-between gap-5">
                <label className="input-label text-gray-400">
                  عدد الحروف {watch("affiliateUrlPrefix")?.length} من 5
                </label>
              </div>
            )}
          </div>

          <div className="sm:col-span-2 xl:col-span-3">
            <Input
              rows={3}
              label="الرسالة"
              type="textarea"
              disabled={show}
              error={errors.messageContent}
              placeholder="اكتب نص الرسالة ..."
              value={watch("messageContent")}
              change={(e) =>
                setValue("messageContent", trimInputValue(e.target.value, 35))
              }
            />

            <div className="mt-3 flex flex-wrap justify-between gap-5">
              <label className="input-label text-gray-400">
                عدد الحروف {watch("messageContent")?.length} من 35
              </label>
            </div>
          </div>
        </div>
        {!show && (
          <Button isLoading={isSubmitting} type="submit" title="حفظ البيانات" />
        )}
      </form>
    </>
  );
};

export default Form;
